import { isObject } from 'lodash-es'

/**
 * Creates a function from a default object that normalizes and validates objects against it.
 * Notes:
 * - All keys from the defaults object are picked from the target recursively, so the result
 *   contains only all keys from the defaults object;
 * - Array properties are not merged;
 * - The validation is done by checking typeof properties against each other. If the property
 *   in the target object doesn't match the default type, then it is overriden by the default;
 * - properties with undefined value in defaults are not validated, i.e. they accept any type
 *   of value;
 * @example
 * ```js
 * const myDefaultObject = { a: 1, b: undefined }
 * const MyObject = normalize(myDefaultObject)
 * MyObject({})                  == { a: 1, b: undefined }
 * MyObject({ b: {} })           == { a: 1, b: {} }
 * MyObject({ a: true })         == { a: 1, b: undefined }
 * MyObject({ unknown: 'prop' }) == { a: 1, b: undefined }
 * ```
 */
export const normalize = <A extends Record<string, any>>(defaults: A) => (obj?: Partial<A>): A => {
  // traverse and normalize properties if it is an object
  if (isObject(defaults) && !Array.isArray(defaults))
    return Object.keys(defaults).reduce(
      (acc: A, k) => {
        if (isObject(obj) && k in obj) acc[k as keyof A] = normalize(defaults[k])(obj[k])
        return acc
      },
      { ...defaults },
    )
  // if a default value is defined and the target value doesn't match, then use the default
  if (typeof defaults !== 'undefined' && typeof defaults !== typeof obj) return defaults
  else return obj as A
}

export const isEmpty = (obj: {}): obj is Record<string, never> => Object.keys(obj).length === 0
